import { Bell, BellOff, BellRing } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";

import { confirmDialog } from "../../../shared/ui/ConfirmHost";
import { usePipelineContext } from "../../../entities/pipeline";
import { needsHomeScreen, usePush } from "../lib/usePush";

/** Роли, которым «Монтаж» шлёт пуши (montageRole на сервере). */
const PUSH_ROLES = ["head", "content_maker", "editor", "moderator"];

/** Шлёт ли «Монтаж» этому человеку пуши — тогда в панели нужно место под кнопку. */
export function useHasPush(): boolean {
  const { divisions } = usePipelineContext();
  return divisions.some((d) => PUSH_ROLES.includes(d.role));
}

export type PushView = {
  icon: typeof Bell;
  label: string;
  on: boolean;
  busy: boolean;
  onClick: () => void;
};

/**
 * Кнопка уведомлений в нижней панели и боковом меню (09.10.2026). Подсказку
 * «Включить» в «Монтаже» можно закрыть крестиком, и после этого включить было
 * негде — на бою из 12 человек подписаны трое.
 *
 * Рендер-проп, а не готовая кнопка: в панели и в меню разная разметка, а
 * поведение одно.
 */
export function PushToggle({ children }: { children: (view: PushView) => ReactNode }) {
  if (!useHasPush()) return null;
  return <PushToggleInner>{children}</PushToggleInner>;
}

function PushToggleInner({ children }: { children: (view: PushView) => ReactNode }) {
  const push = usePush();
  const homeScreen = needsHomeScreen();
  const denied = typeof Notification !== "undefined" && Notification.permission === "denied";
  if (!homeScreen && !push.supported) return null;

  const onClick = () => {
    if (homeScreen) {
      toast.info("На iPhone уведомления работают только из приложения", {
        description: "Поделиться → «На экран „Домой“», затем откройте InstaFarm с экрана «Домой» и нажмите «Пуши».",
        duration: 12_000,
      });
      return;
    }
    if (denied) {
      toast.warning("Уведомления запрещены в настройках браузера", {
        description: "Разрешите их для этого сайта (значок слева от адреса → Уведомления) и нажмите ещё раз.",
        duration: 12_000,
      });
      return;
    }
    if (push.enabled) {
      void confirmDialog({ title: "Выключить уведомления на этом устройстве?", confirmText: "Выключить" })
        .then((ok) => { if (ok) void push.disable(); });
      return;
    }
    void push.enable().then((error) => { if (error) toast.error(error); });
  };

  return (
    <>
      {children({
        icon: push.enabled ? BellRing : denied ? BellOff : Bell,
        label: push.enabled ? "Пуши вкл." : "Пуши",
        on: push.enabled,
        busy: push.busy,
        onClick,
      })}
    </>
  );
}
